const INLINE_MARKERS = ['**', '==', '!!', '^^'];
const INLINE_MARKER_PATTERN = /\*\*|==|!!|\^\^/g;
const INLINE_RANGE_PATTERN = /(\*\*|==|!!|\^\^)([\s\S]*?)\1/g;
const OBSERVATION_TOKEN_PATTERN = /\{\{observation(?::[^{}]+|-(?:start|end):[a-zA-Z0-9_-]+)\}\}/g;
const OBSERVATION_TOKEN_EXACT_PATTERN = /^\{\{observation(?::[^{}]+|-(?:start|end):[a-zA-Z0-9_-]+)\}\}$/;
const PRINTABLE_ASCII_START = 32;
const PRINTABLE_ASCII_END = 126;
const OBSERVATION_EDITOR_CHARACTER_START = 0x0300;

const MARKER_TO_EDITOR_CHARACTER = {
  '**': '\u200B',
  '==': '\u200C',
  '!!': '\u200D',
  '^^': '\u2060',
};

const EDITOR_CHARACTER_TO_MARKER_CHARACTER = {
  '\u200B': '*',
  '\u200C': '=',
  '\u200D': '!',
  '\u2060': '^',
};

function clampIndex(value, length) {
  const number = Number.isFinite(value) ? value : 0;
  return Math.max(0, Math.min(length, number));
}

function collectInlineRanges(value) {
  const ranges = [];
  const regex = new RegExp(INLINE_RANGE_PATTERN.source, 'g');
  let match;
  while ((match = regex.exec(value)) !== null) {
    ranges.push({
      start: match.index,
      end: regex.lastIndex,
      marker: match[1],
    });
  }
  return ranges;
}

function encodeObservationToken(token) {
  return Array.from(token)
    .map((character) => {
      const code = character.charCodeAt(0);
      if (code < PRINTABLE_ASCII_START || code > PRINTABLE_ASCII_END) return character;
      return String.fromCharCode(
        OBSERVATION_EDITOR_CHARACTER_START + code - PRINTABLE_ASCII_START
      );
    })
    .join('');
}

function decodeObservationToken(token) {
  return Array.from(token)
    .map((character) => {
      const code = character.charCodeAt(0);
      const offset = code - OBSERVATION_EDITOR_CHARACTER_START;
      if (offset < 0 || offset > PRINTABLE_ASCII_END - PRINTABLE_ASCII_START) return character;
      return String.fromCharCode(PRINTABLE_ASCII_START + offset);
    })
    .join('');
}

const ENCODED_OBSERVATION_PREFIX = encodeObservationToken('{{observation');
const ENCODED_OBSERVATION_CLOSE = encodeObservationToken('}}');

function decodeObservationTokens(value) {
  const input = String(value || '');
  let output = '';
  let cursor = 0;

  while (cursor < input.length) {
    const tokenStart = input.indexOf(ENCODED_OBSERVATION_PREFIX, cursor);
    if (tokenStart < 0) {
      output += input.slice(cursor);
      break;
    }

    output += input.slice(cursor, tokenStart);
    const tokenEnd = input.indexOf(
      ENCODED_OBSERVATION_CLOSE,
      tokenStart + ENCODED_OBSERVATION_PREFIX.length
    );
    if (tokenEnd < 0) {
      output += input.slice(tokenStart);
      break;
    }

    const candidateEnd = tokenEnd + ENCODED_OBSERVATION_CLOSE.length;
    const encodedCandidate = input.slice(tokenStart, candidateEnd);
    const decodedCandidate = decodeObservationToken(encodedCandidate);
    output += OBSERVATION_TOKEN_EXACT_PATTERN.test(decodedCandidate)
      ? decodedCandidate
      : encodedCandidate;
    cursor = candidateEnd;
  }

  return output;
}

export function stripNotebookInlineMarkers(value) {
  return String(value || '').replace(INLINE_MARKER_PATTERN, '');
}

export function encodeNotebookEditorText(value) {
  const input = String(value || '');
  const regex = new RegExp(INLINE_RANGE_PATTERN.source, 'g');
  const inlineEncoded = input.replace(regex, (match, marker, content) => {
    const hiddenMarker = MARKER_TO_EDITOR_CHARACTER[marker].repeat(marker.length);
    const hiddenContentMarkers = content.replace(
      INLINE_MARKER_PATTERN,
      (nestedMarker) => MARKER_TO_EDITOR_CHARACTER[nestedMarker].repeat(nestedMarker.length)
    );
    return `${hiddenMarker}${hiddenContentMarkers}${hiddenMarker}`;
  });
  return inlineEncoded.replace(
    new RegExp(OBSERVATION_TOKEN_PATTERN.source, 'g'),
    encodeObservationToken
  );
}

export function decodeNotebookEditorText(value) {
  return Array.from(decodeObservationTokens(value))
    .map((character) => EDITOR_CHARACTER_TO_MARKER_CHARACTER[character] || character)
    .join('');
}

export function applyNotebookInlineFormat(value, selectionStart, selectionEnd, marker) {
  const input = String(value || '');
  const markerText = INLINE_MARKERS.includes(marker) ? marker : '';
  if (!markerText) {
    return { value: input, selectionStart, selectionEnd, changed: false };
  }

  let start = clampIndex(selectionStart, input.length);
  let end = clampIndex(selectionEnd, input.length);
  if (end < start) [start, end] = [end, start];

  if (start === end) {
    const nextValue = `${input.slice(0, start)}${markerText}${markerText}${input.slice(end)}`;
    const cursor = start + markerText.length;
    return {
      value: nextValue,
      selectionStart: cursor,
      selectionEnd: cursor,
      changed: true,
    };
  }

  const overlappingRanges = collectInlineRanges(input).filter((range) => (
    end >= range.start && start <= range.end
  ));
  const formatStart = overlappingRanges.length > 0
    ? Math.min(start, ...overlappingRanges.map((range) => range.start))
    : start;
  const formatEnd = overlappingRanges.length > 0
    ? Math.max(end, ...overlappingRanges.map((range) => range.end))
    : end;
  const selectedText = stripNotebookInlineMarkers(input.slice(formatStart, formatEnd));
  const nextValue =
    input.slice(0, formatStart) +
    markerText +
    selectedText +
    markerText +
    input.slice(formatEnd);
  const nextStart = formatStart + markerText.length;

  return {
    value: nextValue,
    selectionStart: nextStart,
    selectionEnd: nextStart + selectedText.length,
    changed: nextValue !== input,
  };
}

export function clearNotebookInlineFormatting(value, selectionStart, selectionEnd) {
  const input = String(value || '');
  let start = clampIndex(selectionStart, input.length);
  let end = clampIndex(selectionEnd, input.length);
  if (end < start) [start, end] = [end, start];
  if (start === end) {
    return { value: input, selectionStart: start, selectionEnd: end, changed: false };
  }

  const overlappingRanges = collectInlineRanges(input).filter((range) => (
    end >= range.start && start <= range.end
  ));
  const clearStart = overlappingRanges.length > 0
    ? Math.min(start, ...overlappingRanges.map((range) => range.start))
    : start;
  const clearEnd = overlappingRanges.length > 0
    ? Math.max(end, ...overlappingRanges.map((range) => range.end))
    : end;
  const clearedText = stripNotebookInlineMarkers(input.slice(clearStart, clearEnd));
  const nextValue = input.slice(0, clearStart) + clearedText + input.slice(clearEnd);

  return {
    value: nextValue,
    selectionStart: clearStart,
    selectionEnd: clearStart + clearedText.length,
    changed: nextValue !== input,
  };
}
